import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { adminImportRankings, adminListRankings, adminDeleteRanking, API } from "../lib/api";

async function gscStatus(key) {
  const r = await fetch(`${API}/admin/rankings/gsc/status`, { headers: { "X-Admin-Key": key } });
  if (!r.ok) throw new Error("status failed");
  return r.json();
}
async function gscStart(key, body) {
  const r = await fetch(`${API}/admin/rankings/gsc/oauth/start`, {
    method: "POST", headers: { "X-Admin-Key": key, "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.detail || "connect failed");
  return data;
}
async function gscSync(key) {
  const r = await fetch(`${API}/admin/rankings/gsc/sync`, { method: "POST", headers: { "X-Admin-Key": key } });
  const data = await r.json();
  if (!r.ok) throw new Error(data.detail || "sync failed");
  return data;
}
async function gscDisconnect(key) {
  const r = await fetch(`${API}/admin/rankings/gsc/disconnect`, { method: "POST", headers: { "X-Admin-Key": key } });
  if (!r.ok) throw new Error("disconnect failed");
  return r.json();
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function DeltaChip({ value, invert = false }) {
  if (value === null || value === undefined) return <span className="text-rw-ink3 text-xs">·</span>;
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  if (value === 0) return <span className="text-rw-ink3 text-xs">±0</span>;
  const cls = positive
    ? "bg-green-100 text-green-800"
    : negative
    ? "bg-red-100 text-red-800"
    : "bg-rw-bg3 text-rw-ink2";
  const sign = value > 0 ? "+" : "";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-mono font-semibold ${cls}`}>{sign}{Number(value).toFixed(1)}</span>;
}

export default function RankingsTab({ adminKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csv, setCsv] = useState("");
  const [label, setLabel] = useState("");
  const [importing, setImporting] = useState(false);
  const [gsc, setGsc] = useState(null);
  const [gscForm, setGscForm] = useState({
    client_id: "", client_secret: "", site_url: "https://riznwash.com/",
  });
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadGsc = useCallback(async () => {
    try { setGsc(await gscStatus(adminKey)); } catch { setGsc(null); }
  }, [adminKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminListRankings(adminKey);
      setData(d);
    } catch {
      toast.error("Couldn't load rankings");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); loadGsc(); }, [load, loadGsc]);

  const handleImport = async (e) => {
    e?.preventDefault?.();
    if (!csv.trim()) {
      toast.error("Paste a CSV first");
      return;
    }
    setImporting(true);
    try {
      await adminImportRankings(adminKey, csv, label.trim() || undefined);
      toast.success("Snapshot imported");
      setCsv("");
      setLabel("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsv(text);
    if (!label && f.name) setLabel(f.name.replace(/\.csv$/i, ""));
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this snapshot?")) return;
    try {
      await adminDeleteRanking(adminKey, id);
      toast.success("Snapshot deleted");
      load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const handleGscConnect = async () => {
    if (!gscForm.client_id.trim() || !gscForm.client_secret.trim()) {
      toast.error("Paste both Client ID and Client Secret");
      return;
    }
    setConnecting(true);
    try {
      const res = await gscStart(adminKey, gscForm);
      toast.success("Redirecting to Google…");
      // Open Google consent in a new tab; poll for status until it flips
      window.open(res.auth_url, "_blank", "noopener,noreferrer");
      // Poll every 3s for up to 5 minutes
      let tries = 0;
      const t = setInterval(async () => {
        tries += 1;
        const s = await gscStatus(adminKey).catch(() => null);
        if (s?.connected) {
          clearInterval(t);
          setGsc(s);
          toast.success(`Connected as ${s.connected_email || "your Google account"}`);
        } else if (tries > 100) {
          clearInterval(t);
        }
      }, 3000);
    } catch (e) {
      toast.error(e.message || "Connect failed");
    } finally {
      setConnecting(false);
    }
  };

  const handleGscSync = async () => {
    setSyncing(true);
    try {
      const res = await gscSync(adminKey);
      toast.success(`Synced ${res.rows} queries from Search Console`);
      loadGsc();
      load();
    } catch (e) {
      toast.error(e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleGscDisconnect = async () => {
    if (!confirm("Disconnect Search Console? Auto-sync will stop until you reconnect.")) return;
    try {
      await gscDisconnect(adminKey);
      setGsc({ connected: false });
      toast.success("Disconnected");
    } catch {
      toast.error("Couldn't disconnect");
    }
  };

  const rows = data?.delta || [];
  const hasPrev = !!data?.previous;

  return (
    <div className="mt-8" data-testid="rankings-tab">
      {/* Google Search Console connection */}
      <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 md:p-8 mb-6" data-testid="gsc-panel">
        {gsc?.connected ? (
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                <span className="overline text-green-700">Google Search Console connected</span>
              </div>
              <h3 className="font-display text-2xl leading-tight">{gsc.connected_email || "Connected"}</h3>
              <div className="mt-2 text-sm text-rw-ink2 space-y-1">
                <div>Property: <span className="font-mono">{gsc.site_url}</span></div>
                <div>
                  {gsc.last_sync_at
                    ? <>Last synced {fmtDate(gsc.last_sync_at)} · {gsc.last_sync_row_count || 0} queries</>
                    : <>No sync yet — hit "Sync now" to pull yesterday's data</>}
                </div>
                <div className="text-xs text-rw-ink3">Auto-sync runs daily at 03:15 UTC (08:45 IST)</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <motion.button
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                onClick={handleGscSync}
                disabled={syncing}
                className="btn-dark rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                data-testid="gsc-sync-btn"
              >
                {syncing ? "Syncing…" : "Sync now →"}
              </motion.button>
              <button
                onClick={handleGscDisconnect}
                className="rounded-full border border-rw-ink/15 px-5 py-2.5 text-sm font-semibold hover:bg-rw-bg3"
                data-testid="gsc-disconnect-btn"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <div className="overline mb-2">Auto-sync (no more Monday CSV)</div>
              <h3 className="font-display text-2xl leading-tight">Connect Google Search Console.</h3>
              <p className="text-sm text-rw-ink2 mt-3 leading-relaxed">
                We'll pull yesterday's top queries every night at 03:15 UTC and store them here as a snapshot,
                so week-over-week deltas populate automatically.
              </p>
              <details className="mt-4 text-xs text-rw-ink3">
                <summary className="cursor-pointer hover:text-rw-ink font-semibold">One-time setup steps</summary>
                <ol className="mt-2 space-y-1 list-decimal list-inside">
                  <li>Google Cloud → APIs &amp; Services → Enable <em>Google Search Console API</em></li>
                  <li>OAuth consent screen → External → add scope <span className="font-mono">webmasters.readonly</span> → add your email as a test user</li>
                  <li>Credentials → Create OAuth 2.0 Client → <em>Web application</em></li>
                  <li>Add authorized redirect URI: <span className="font-mono break-all">{`${API}/admin/rankings/gsc/oauth/callback`}</span></li>
                  <li>Copy the Client ID + Client Secret and paste them here</li>
                </ol>
              </details>
            </div>
            <div className="md:col-span-2 space-y-3">
              <input
                type="text"
                value={gscForm.client_id}
                onChange={(e) => setGscForm({ ...gscForm, client_id: e.target.value })}
                placeholder="OAuth Client ID (ends in .apps.googleusercontent.com)"
                className="w-full rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-rw-blue bg-white"
                data-testid="gsc-client-id"
              />
              <input
                type="password"
                value={gscForm.client_secret}
                onChange={(e) => setGscForm({ ...gscForm, client_secret: e.target.value })}
                placeholder="OAuth Client Secret"
                className="w-full rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-rw-blue bg-white"
                data-testid="gsc-client-secret"
              />
              <input
                type="text"
                value={gscForm.site_url}
                onChange={(e) => setGscForm({ ...gscForm, site_url: e.target.value })}
                placeholder="Search Console property URL (with trailing /)"
                className="w-full rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-rw-blue bg-white font-mono"
                data-testid="gsc-site-url"
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-rw-ink3">
                  Credentials stay server-side — never sent to the browser after this.
                </div>
                <motion.button
                  whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                  onClick={handleGscConnect}
                  disabled={connecting}
                  className="btn-dark rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
                  data-testid="gsc-connect-btn"
                >
                  {connecting ? "Opening Google…" : "Connect Search Console →"}
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Import panel */}
      <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 md:p-8">
        <div className="grid md:grid-cols-3 gap-6 md:gap-10">
          <div className="md:col-span-1">
            <div className="overline mb-2">Search Console import</div>
            <h3 className="font-display text-2xl leading-tight">Paste this week's top-queries CSV.</h3>
            <p className="text-sm text-rw-ink2 mt-3 leading-relaxed">
              In Search Console → <em>Performance → Queries</em> → click <em>Export → CSV</em>. Open the file and
              paste the contents below, or upload the file directly.
            </p>
            <p className="text-xs text-rw-ink3 mt-3">
              Expected columns: <span className="font-mono">Top queries, Clicks, Impressions, CTR, Position</span>
            </p>
          </div>
          <form onSubmit={handleImport} className="md:col-span-2 space-y-3" data-testid="rankings-import-form">
            <div className="flex flex-wrap gap-3">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. Week of 20 Aug)"
                className="flex-1 rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-rw-blue bg-white"
                data-testid="rankings-label-input"
              />
              <label className="rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm font-semibold cursor-pointer hover:bg-rw-bg3 flex items-center" data-testid="rankings-upload-btn">
                Upload CSV
                <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              </label>
            </div>
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={8}
              placeholder={`Top queries,Clicks,Impressions,CTR,Position\nlaundry gurugram,142,3210,4.42%,3.8\n...`}
              className="w-full rounded-xl border border-rw-ink/15 px-4 py-3 text-sm font-mono focus:outline-none focus:border-rw-blue bg-white"
              data-testid="rankings-csv-input"
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-rw-ink3">
                {csv ? `${csv.split(/\r?\n/).filter(l => l.trim()).length - 1} data rows` : "No rows yet"}
              </div>
              <motion.button
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                type="submit"
                disabled={importing || !csv.trim()}
                className="btn-dark rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
                data-testid="rankings-import-submit"
              >
                {importing ? "Importing…" : "Import snapshot →"}
              </motion.button>
            </div>
          </form>
        </div>
      </div>

      {/* Snapshot summary */}
      {loading && !data && (
        <div className="mt-8 text-rw-ink3 text-sm">Loading rankings…</div>
      )}

      {data?.latest && (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border border-rw-ink/10 rounded-2xl overflow-hidden bg-rw-bg2" data-testid="rankings-summary">
            <div className="p-5 border-r border-b md:border-b-0 border-rw-ink/10">
              <div className="overline">Latest snapshot</div>
              <div className="font-display text-xl mt-1">{data.latest.label || fmtDate(data.latest.created_at)}</div>
              <div className="text-xs text-rw-ink3 mt-1">{fmtDate(data.latest.created_at)}</div>
            </div>
            <div className="p-5 border-b md:border-b-0 md:border-r border-rw-ink/10">
              <div className="overline">Total clicks</div>
              <div className="font-display text-2xl mt-1">{data.latest.total_clicks}</div>
              {hasPrev && (
                <div className="text-xs mt-1">
                  <DeltaChip value={data.latest.total_clicks - data.previous.total_clicks} />
                  <span className="ml-2 text-rw-ink3">vs previous</span>
                </div>
              )}
            </div>
            <div className="p-5 border-r border-rw-ink/10">
              <div className="overline">Impressions</div>
              <div className="font-display text-2xl mt-1">{data.latest.total_impressions.toLocaleString("en-IN")}</div>
              {hasPrev && (
                <div className="text-xs mt-1">
                  <DeltaChip value={data.latest.total_impressions - data.previous.total_impressions} />
                  <span className="ml-2 text-rw-ink3">vs previous</span>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="overline">Queries tracked</div>
              <div className="font-display text-2xl mt-1">{data.latest.row_count}</div>
              <div className="text-xs text-rw-ink3 mt-1">across {data.snapshots.length} snapshots</div>
            </div>
          </div>

          {/* Movers */}
          {hasPrev && (
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="rankings-gainers">
                <div className="overline text-green-700">🎉 Position gainers</div>
                <ul className="mt-3 divide-y divide-rw-ink/10">
                  {data.top_gainers.length === 0 && <li className="py-3 text-sm text-rw-ink3">No gainers this week</li>}
                  {data.top_gainers.slice(0, 5).map((r) => (
                    <li key={r.query} className="py-3 flex items-baseline gap-3">
                      <span className="flex-1 text-sm font-medium truncate">{r.query}</span>
                      <span className="text-xs text-rw-ink3 tabular-nums">{r.prev_position?.toFixed(1)} → {r.position.toFixed(1)}</span>
                      <DeltaChip value={r.position_delta} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="rankings-losers">
                <div className="overline text-red-700">Position losers</div>
                <ul className="mt-3 divide-y divide-rw-ink/10">
                  {data.top_losers.length === 0 && <li className="py-3 text-sm text-rw-ink3">Everyone held or gained ground 🎉</li>}
                  {data.top_losers.slice(0, 5).map((r) => (
                    <li key={r.query} className="py-3 flex items-baseline gap-3">
                      <span className="flex-1 text-sm font-medium truncate">{r.query}</span>
                      <span className="text-xs text-rw-ink3 tabular-nums">{r.prev_position?.toFixed(1)} → {r.position.toFixed(1)}</span>
                      <DeltaChip value={r.position_delta} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Full table */}
          <div className="mt-8 rounded-2xl border border-rw-ink/10 overflow-hidden bg-rw-bg2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rankings-table">
                <thead className="bg-rw-bg3 text-rw-ink3">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Query</th>
                    <th className="text-right px-4 py-3 font-semibold">Clicks</th>
                    <th className="text-right px-4 py-3 font-semibold">Impressions</th>
                    <th className="text-right px-4 py-3 font-semibold">Position</th>
                    <th className="text-right px-4 py-3 font-semibold">Δ Position</th>
                    <th className="text-right px-4 py-3 font-semibold">Δ Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-rw-ink3">Import a snapshot to see queries.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.query} className="border-t border-rw-ink/10">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.query}</div>
                        {r.is_new && <span className="inline-block mt-1 rounded-full bg-rw-blue/20 text-rw-ink text-[10px] font-semibold px-2 py-0.5">NEW</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.clicks}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.impressions.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.position.toFixed(1)}</td>
                      <td className="px-4 py-3 text-right"><DeltaChip value={r.position_delta} /></td>
                      <td className="px-4 py-3 text-right"><DeltaChip value={r.clicks_delta} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Snapshot history */}
          <div className="mt-8">
            <div className="overline mb-3">Snapshot history</div>
            <ul className="divide-y divide-rw-ink/10 border border-rw-ink/10 rounded-2xl overflow-hidden bg-rw-bg2">
              {data.snapshots.map((s) => (
                <li key={s.id} className="flex items-center gap-4 px-5 py-3" data-testid={`rankings-snap-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{s.label || fmtDate(s.created_at)}</span>
                      {s.source === "gsc-api" && (
                        <span className="inline-block rounded-full bg-rw-blue/20 text-rw-ink text-[10px] font-semibold px-2 py-0.5" data-testid={`snap-source-${s.id}`}>
                          AUTO-SYNCED
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-rw-ink3">{fmtDate(s.created_at)} · {s.row_count} queries · {s.total_clicks} clicks</div>
                  </div>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="text-xs text-rw-ink3 hover:text-red-600 font-semibold"
                    data-testid={`rankings-snap-del-${s.id}`}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {!loading && !data?.latest && (
        <div className="mt-10 text-center text-rw-ink3 py-16 border border-dashed border-rw-ink/15 rounded-2xl">
          <div className="font-display text-xl text-rw-ink mb-2">No snapshots yet</div>
          <div className="text-sm">Paste your first Search Console export above to start tracking movement.</div>
        </div>
      )}
    </div>
  );
}
